import React from 'react'
import { Link as ReactRouterLink } from 'react-router-dom'
import { Alert, StackView } from '@planningcenter/tapestry-react'
import Apps from '../../../../api/apps'
import Table from '../../../shared/table'
import {
  Code,
  CodeBlock as TypographyCodeBlock,
  Heading,
  Link as SharedLink,
  List,
  PageHeading,
  Text,
} from '../../../shared/typography'

export default function page() {
  return (
    <StackView axis="vertical" spacing={2}>
      <PageHeading>OAuth 2.0</PageHeading>

      <Text>
        If you are distributing your app to multiple churches, you should use{' '}
        <Link to="https://en.wikipedia.org/wiki/OAuth" external>
          OAuth
        </Link>{' '}
        version 2.0.
      </Text>

      <Text>
        First, your company needs to have a Planning Center account, which
        represents your organization. You can{' '}
        <Link href="https://accounts.planningcenteronline.com/sign_up" external>
          create one here
        </Link>
        . This feels a little funny at first, because it is like you are signing
        up as a church for Planning Center. But having a Planning Center account
        with a login gives you a place to manage your OAuth application. The
        name you give your Planning Center account (the "church" name) can just
        be your company name.
      </Text>

      <Text>
        Once you are logged into your Planning Center account, visit{' '}
        <Link to="https://api.planningcenteronline.com" external>
          api.planningcenteronline.com
        </Link>{' '}
        to create an OAuth application. This client ID and secret will only be
        created once, and will be reused for every church that signs up for your
        integration.
      </Text>

      <Alert type="warning" marginBottom={2}>
        <Text size={4}>
          <strong>Important:</strong> Only create one OAuth application. When
          using OAuth, churches do <strong>not</strong> need to create their own
          OAuth application. You can integrate with multiple churches using your
          single OAuth application that you control.
        </Text>
      </Alert>

      <Alert stroke="grey-6" backgroundColor="grey-4" marginBottom={2}>
        <Text size={4}>
          Starting March 22, 2023, only Organization Administrators can create
          OAuth applications, and all new applications are managed by all
          Organization Administrators in your organization. If you are part of a
          church you should consider{' '}
          <Link to="https://www.planningcenter.com/pricing">
            creating a new free organization
          </Link>{' '}
          to hold your application if the application doesn't belong to that
          church.
        </Text>
      </Alert>

      <Text>
        Make sure your app conforms to the{' '}
        <Link to="http://oauth.net/2/" external>
          OAuth 2.0
        </Link>{' '}
        specification. Generally, to authenticate a user, follow these steps:
      </Text>

      <List ordered>
        <List.Item>
          <StackView axis="vertical" spacing={1}>
            <Text>Redirect the user’s browser to</Text>
            <CodeBlock>
              https://api.planningcenteronline.com/oauth/authorize?client_id=CLIENT_ID&redirect_uri=https://example.com/auth/complete&response_type=code&scope=people
            </CodeBlock>
            <Text>
              Replace <Code>CLIENT_ID</Code> and{' '}
              <Code>https://example.com/auth/complete</Code> with your actual
              redirect URI. If you need different scopes, replace{' '}
              <Code>scope=people</Code> appropriately (see{' '}
              <Link to="/overview/authentication/oauth-2-0#scopes">
                Application Scopes
              </Link>
              ).
            </Text>
          </StackView>
        </List.Item>

        <List.Item>
          <Text>
            Planning Center will redirect the user’s browser back to the given
            redirect URI with a <Code>code</Code> param.
          </Text>
        </List.Item>

        <List.Item>
          <StackView axis="vertical" spacing={1}>
            <Text>
              Send a POST request in the background to{' '}
              <Code>https://api.planningcenteronline.com/oauth/token</Code> with
              the following params:{' '}
            </Text>
            <CodeBlock language="bash">
              {`
{
  "grant_type": "authorization_code",
  "code": "CODE_FROM_STEP_2",
  "client_id": "CLIENT_ID",
  "client_secret": "CLIENT_SECRET",
  "redirect_uri": "https://example.com/auth/complete"
}
            `.trim()}
            </CodeBlock>
            <Text>
              Replace <Code>CLIENT_ID</Code>, <Code>CLIENT_SECRET</Code>,{' '}
              <Code>CODE_FROM_STEP_2</Code>, and the redirect URI appropriately.
            </Text>

            <CodeBlock language="bash">
              {`
curl -X POST https://api.planningcenteronline.com/oauth/token \\
  -F grant_type=authorization_code \\
  -F code=1234567890 \\
  -F client_id=2345678901 \\
  -F client_secret=3456789012 \\
  -F redirect_uri=https://example.com/auth/complete
            `.trim()}
            </CodeBlock>
          </StackView>
        </List.Item>

        <List.Item>
          <StackView axis="vertical" spacing={1}>
            <Text>
              The response you get back will contain the access token and other
              information.
            </Text>

            <CodeBlock language="json">
              {JSON.stringify(
                {
                  access_token:
                    '1234567890abcdef1234567890abcdef1234567890abcdef1234567890abcdef',
                  token_type: 'bearer',
                  expires_in: 7200,
                  refresh_token:
                    '1234567890abcdef1234567890abcdef1234567890abcdef1234567890abcdef',
                  scope: 'people',
                  created_at: 1469553476,
                },
                null,
                2
              )}
            </CodeBlock>
          </StackView>
        </List.Item>

        <List.Item>
          <StackView axis="vertical" spacing={1}>
            <Text>
              Use the access token for all API requests by passing it in the{' '}
              <Code>Authorization</Code> header, using the <Code>Bearer</Code>{' '}
              authentication scheme.
            </Text>
            <CodeBlock language="bash">
              {`
curl https://api.planningcenteronline.com/people/v2/people \\
  -H 'Authorization: Bearer pco_tok_97df13b9'
              `.trim()}
            </CodeBlock>
          </StackView>
        </List.Item>
      </List>

      <Text>
        OAuth access tokens expire after 2 hours from the time they are issued,
        but we also provide a{' '}
        <Link to="https://tools.ietf.org/html/rfc6749#section-1.5" external>
          refresh token
        </Link>{' '}
        you can use to get a new access token at any time, even after the access
        token expires, without forcing the user to re-authorize.
      </Text>
      <Text>
        We will honor refresh tokens for up to 90 days after the date its
        associated access token was issued. This means that, if your app does
        not issue a refresh for 90 days, then your user will need to
        re-authorize.
      </Text>

      <Heading anchored id="scopes" size={1}>
        Application Scopes
      </Heading>

      <Text>
        Each Planning Center product is a distinct{' '}
        <Link to="http://tools.ietf.org/html/rfc6749#section-3.3" external>
          OAuth scope
        </Link>
        .
      </Text>

      <ScopesTable />

      <Text>
        <em>
          Note that accessing Webhooks endpoints do not require an OAuth scope.
        </em>
      </Text>

      <Text>
        Authorization URL is{' '}
        <Link
          to="https://api.planningcenteronline.com/oauth/authorize"
          external
        >
          https://api.planningcenteronline.com/oauth/authorize
        </Link>{' '}
        Token URL is{' '}
        <Link to="https://api.planningcenteronline.com/oauth/token" external>
          https://api.planningcenteronline.com/oauth/token
        </Link>{' '}
        When authorizing, you can request scopes using the <Code>scope</Code>{' '}
        parameter. The value should be a space-separated list of scopes. The
        value for <Code>response_type</Code> should be <Code>code</Code>.
      </Text>

      <Heading anchored id="refreshing-a-token" size={1}>
        Refreshing a token
      </Heading>

      <Text>
        You can refresh an expired Oauth token by using the{' '}
        <Code>refresh_token</Code> returned when you generated the token.
      </Text>

      <CodeBlock language="bash">
        {`
curl -X "POST" "https://api.planningcenteronline.com/oauth/token" \\
  -H 'Content-Type: application/json' \\
  -d $'{
    "client_id": "<your application client id>",
    "client_secret": "<your application secret>",
    "refresh_token": "<your access token refresh token>",
    "grant_type": "refresh_token"
  }'
        `.trim()}
      </CodeBlock>
      <Text>The response body will look like this:</Text>
      <CodeBlock language="json">
        {JSON.stringify(
          {
            access_token: '<new access token>',
            token_type: 'bearer',
            expires_in: 7200,
            refresh_token: '<new refresh token>',
            created_at: 1540325919,
          },
          null,
          2
        )}
      </CodeBlock>
    </StackView>
  )
}

function ScopesTable() {
  const columns = [
    {
      header: 'Product',
      cell: 'name',
      css: { flex: '1 1 0' },
    },
    {
      header: 'Scope',
      cell: row => <Code>{row.scope}</Code>,
      css: { flex: '1 1 0' },
    },
  ]

  const data = Apps.filter(({ scope }) => scope).map(({ name, scope }) => ({
    name,
    scope,
  }))

  return <Table columns={columns} data={data} />
}

function Link({ external, ...props }) {
  return external ? (
    <SharedLink external {...props} />
  ) : (
    <ReactRouterLink component={SharedLink} {...props} />
  )
}

function CodeBlock(props) {
  return (
    <TypographyCodeBlock
      customStyle={{ marginTop: 0, marginBottom: 0 }}
      {...props}
    />
  )
}
